// src/components/modals/EnviarCerasFundicaoModal.js
import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Package,
  CheckCircle2,
  AlertCircle,
  Loader2,
  MapPin,
  Tag,
  Disc,
  Plus,
  Minus,
  Info,
  Layers,
  Sparkles
} from "lucide-react";
import { ceraFundicaoService, generateEstoqueOrderNumber } from "../../services/ceraFundicaoService";
import { cleanSku, getBaseSku } from "../../utils/skuUtils";

/**
 * EnviarCerasFundicaoModal.js
 * 
 * Modal único e direto para Solicitação de Produção de Estoque via Molde.
 * Sem subabas: quem solicita apenas define a quantidade (e aro, se anel).
 * A decisão de qual tipo de cera usar (bruta, pré-cravada) ou se aproveita
 * ceras existentes na gaveta é tomada diretamente pela equipe na área de produção.
 */
export default function EnviarCerasFundicaoModal({
  isOpen,
  onClose,
  item,
  user,
  onSuccess
}) {
  const [qtd, setQtd] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const [observacao, setObservacao] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: "success" | "error", message: string }
  const [orderNumberPreview, setOrderNumberPreview] = useState("");

  // Normaliza dados do item
  const sku = item?.sku || item?.skuPai || "";
  const skuPai = getBaseSku(cleanSku(sku)) || sku;
  const name = item?.name || item?.nome || item?.title || sku;
  const image = item?.image || item?.image_url || item?.foto || "";
  const gaveta = item?.gaveta || item?.localizacao_gaveta || item?.subestoque?.localizacao_gaveta || "N/I";

  const estoqueBruta = Number(
    item?.cera_bruta ??
    item?.subestoque?.cera_bruta ??
    item?.ceraBruta ??
    0
  );

  const estoquePrecravada = Number(
    item?.cera_precravada ??
    item?.subestoque?.cera_precravada ??
    item?.ceraPrecravada ??
    0
  );

  const totalEstoqueDisponivel = estoqueBruta + estoquePrecravada;

  // Mapa de estoque por aro (se existir)
  const estoquePorAro = useMemo(() => {
    return item?.estoque_por_aro || item?.subestoque?.estoque_por_aro || {};
  }, [item]);

  const isAnel = useMemo(() => {
    const s = (sku + " " + name + " " + (item?.tipo_modelo || "")).toLowerCase();
    return s.includes("anel") || s.includes("alianca") || s.includes("aliança");
  }, [sku, name, item]);

  const hasArosCadastrados = useMemo(() => {
    return (
      Object.keys(estoquePorAro).length > 0 ||
      (Array.isArray(item?.config_borracha?.aros_disponiveis) && item.config_borracha.aros_disponiveis.length > 0)
    );
  }, [estoquePorAro, item]);

  // Lista consolidada de aros disponíveis para o molde
  const arosList = useMemo(() => {
    const set = new Set();
    Object.keys(estoquePorAro).forEach(k => set.add(String(k).trim()));
    if (Array.isArray(item?.config_borracha?.aros_disponiveis)) {
      item.config_borracha.aros_disponiveis.forEach(a => set.add(String(a).trim()));
    }
    return Array.from(set).sort((a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0));
  }, [estoquePorAro, item]);

  // Saldo específico do aro selecionado (apenas informativo)
  const activeAroStock = useMemo(() => {
    if (!selectedSize) return null;
    const cleanAro = String(selectedSize).trim();
    if (estoquePorAro[cleanAro]) {
      return {
        bruta: Number(estoquePorAro[cleanAro].bruta) || 0,
        precravada: Number(estoquePorAro[cleanAro].precravada) || 0
      };
    }
    return null;
  }, [selectedSize, estoquePorAro]);

  // Inicialização ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      setOrderNumberPreview(generateEstoqueOrderNumber());
      setSelectedSize(item?.size || item?.aro || "");
      setQtd(1);
      setObservacao("");
      setFeedback(null);
    }
  }, [isOpen, item]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);

    const numQtd = Math.max(1, parseInt(qtd, 10) || 1);
    if (numQtd <= 0) {
      setFeedback({ type: "error", message: "Informe uma quantidade válida para produção." });
      return;
    }

    setSubmitting(true);
    try {
      const res = await ceraFundicaoService.solicitarProducaoEstoque({
        sku,
        productName: name,
        qtd: numQtd,
        size: selectedSize || null,
        gaveta: gaveta === "N/I" ? "" : gaveta,
        observacao: observacao.trim(),
        userName: user?.name || user?.username || "Operador"
      });

      setFeedback({
        type: "success",
        message: `Solicitação #${res.orderNumber} enviada para a produção com sucesso! (${res.totalQtd} peça(s) na fila de moldes).`
      });

      if (onSuccess) onSuccess(res);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error("Erro ao solicitar produção para estoque:", err);
      setFeedback({
        type: "error",
        message: err.message || "Falha ao gerar solicitação de produção."
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col my-auto transition-all">
        {/* HEADER */}
        <div className="px-6 py-4 bg-gradient-to-r from-amber-600 via-amber-700 to-orange-700 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-white/10 rounded-xl backdrop-blur-xs border border-white/20">
              <Layers size={20} className="text-amber-200" />
            </div>
            <div>
              <h3 className="font-black text-base tracking-tight flex items-center gap-2">
                Solicitar Produção para Estoque
              </h3>
              <p className="text-xs text-amber-100/90 font-medium">
                Gera ordem de fabricação para a fila de moldes da fábrica
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-1.5 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* FEEDBACK ALERT */}
        {feedback && (
          <div
            className={`px-5 py-3 text-xs font-semibold flex items-center gap-2 border-b ${
              feedback.type === "success"
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : "bg-rose-50 text-rose-800 border-rose-200"
            }`}
          >
            {feedback.type === "success" ? (
              <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle size={16} className="text-rose-600 shrink-0" />
            )}
            <span>{feedback.message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* CARD DA JOIA */}
          <div className="flex items-center gap-3.5 p-3 rounded-xl bg-slate-50 border border-slate-200/80">
            {image ? (
              <img
                src={image}
                alt={name}
                className="w-14 h-14 object-cover rounded-lg border border-slate-200 bg-white shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                <Package size={24} />
              </div>
            )}
            <div className="flex-1 min-w-0 space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">
                  {sku}
                </span>
                <span className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded flex items-center gap-1">
                  <MapPin size={11} /> Gaveta: {gaveta}
                </span>
              </div>
              <h4 className="text-xs font-bold text-slate-800 truncate" title={name}>
                {name}
              </h4>
              <div className="text-[11px] text-slate-500 font-medium">
                Saldo na Gaveta:{" "}
                <strong className={totalEstoqueDisponivel > 0 ? "text-amber-900 font-black" : "text-slate-600"}>
                  {totalEstoqueDisponivel} un
                </strong>{" "}
                ({estoqueBruta} brutas • {estoquePrecravada} pré-cravadas)
              </div>
            </div>
          </div>

          {/* SELEÇÃO DE ARO / TAMANHO (Se for Anel ou tiver aros) */}
          {(isAnel || hasArosCadastrados) && (
            <div className="p-3 rounded-xl border border-indigo-100 bg-indigo-50/50 space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-indigo-900 flex items-center gap-1.5">
                  <Tag size={13} className="text-indigo-600" />
                  Medida / Aro do Anel
                </label>
                {selectedSize && (
                  <button
                    type="button"
                    onClick={() => setSelectedSize("")}
                    className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 underline cursor-pointer"
                  >
                    Limpar aro
                  </button>
                )}
              </div>

              {/* Chips dos aros */}
              {arosList.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {arosList.map((aro) => {
                    const isSelected = String(selectedSize).trim() === String(aro).trim();
                    const st = estoquePorAro[aro] || { bruta: 0, precravada: 0 };
                    const hasStock = st.bruta > 0 || st.precravada > 0;

                    return (
                      <button
                        key={aro}
                        type="button"
                        onClick={() => setSelectedSize(isSelected ? "" : aro)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer select-none ${
                          isSelected
                            ? "bg-indigo-600 text-white shadow-xs"
                            : "bg-white text-indigo-950 border border-indigo-200 hover:bg-indigo-100/70"
                        }`}
                      >
                        <span>Aro {aro}</span>
                        {hasStock && (
                          <span
                            className={`text-[9px] px-1 rounded ${
                              isSelected ? "bg-indigo-700 text-white" : "bg-amber-100 text-amber-900 font-extrabold"
                            }`}
                            title={`Gaveta: ${st.bruta + st.precravada} ceras (${st.bruta}b / ${st.precravada}c)`}
                          >
                            {st.bruta + st.precravada} na gaveta
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Input de aro customizado */}
              <div className="flex items-center gap-2 pt-0.5">
                <input
                  type="text"
                  placeholder="Ou digite outro aro (ex: 15, 23, 27...)"
                  value={selectedSize}
                  onChange={(e) => setSelectedSize(e.target.value)}
                  disabled={submitting}
                  className="w-full text-xs font-bold text-slate-800 px-3 py-1.5 bg-white border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-hidden"
                />
              </div>

              {activeAroStock && (
                <div className="text-[11px] text-indigo-900 bg-indigo-100/70 px-2.5 py-1 rounded-md font-medium">
                  Saldo existente no <strong>Aro {selectedSize}</strong>: {activeAroStock.bruta} brutas • {activeAroStock.precravada} pré-cravadas
                </div>
              )}
            </div>
          )}

          {/* QUANTIDADE SOLICITADA */}
          <div className="p-3.5 rounded-xl border border-amber-200 bg-amber-50/30 shadow-xs space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Disc size={15} className="text-amber-600" />
                Quantidade a Produzir
              </label>
              <span className="text-[10px] font-bold text-slate-400">
                Peças para o estoque
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center border border-slate-300 rounded-xl overflow-hidden bg-white flex-1 shadow-2xs">
                <button
                  type="button"
                  onClick={() => setQtd((prev) => Math.max(1, (parseInt(prev, 10) || 1) - 1))}
                  disabled={qtd <= 1 || submitting}
                  className="p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-40 transition cursor-pointer"
                >
                  <Minus size={15} />
                </button>
                <input
                  type="number"
                  min="1"
                  value={qtd}
                  onChange={(e) => setQtd(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  disabled={submitting}
                  className="w-full text-center font-black text-base text-slate-900 bg-transparent focus:outline-hidden"
                />
                <button
                  type="button"
                  onClick={() => setQtd((prev) => (parseInt(prev, 10) || 1) + 1)}
                  disabled={submitting}
                  className="p-2 text-slate-600 hover:bg-slate-100 transition cursor-pointer"
                >
                  <Plus size={15} />
                </button>
              </div>

              {/* Atalhos rápidos */}
              <div className="flex items-center gap-1">
                {[+1, +2, +5, +10].map((delta) => (
                  <button
                    key={delta}
                    type="button"
                    onClick={() => setQtd((prev) => (parseInt(prev, 10) || 1) + delta)}
                    disabled={submitting}
                    className="px-2.5 py-2 text-xs font-bold text-amber-900 bg-amber-100/80 hover:bg-amber-200/80 border border-amber-300 rounded-xl transition cursor-pointer"
                  >
                    +{delta}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* OBSERVAÇÃO OPCIONAL */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-700">
              Observações para a Produção (Opcional):
            </label>
            <input
              type="text"
              placeholder="Ex: Reposição vitrine, pedido especial, prioridade..."
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              disabled={submitting}
              className="w-full text-xs text-slate-800 px-3 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:outline-hidden"
            />
          </div>

          {/* PRÉVIA TRANSPARENTE DA ORDEM GERADA */}
          <div className="p-3.5 rounded-xl bg-slate-900 text-slate-100 space-y-2 text-xs">
            <div className="flex items-center justify-between text-[11px] text-slate-400 uppercase tracking-wider font-bold">
              <span>Prévia da Solicitação</span>
              <span className="text-amber-400">Fila de Produção</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs pt-1 border-t border-slate-800">
              <div>
                <span className="text-slate-400 block text-[10px]">Número do Pedido:</span>
                <span className="font-mono font-bold text-white tracking-wide">
                  {orderNumberPreview || "EST-..."}
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px]">Cliente:</span>
                <span className="font-bold text-white">Solicitacao-Estoque</span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px]">Destino Inicial:</span>
                <span className="inline-block px-1.5 py-0.5 rounded font-bold text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  IMPRIMIR (Fila de Injeção)
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px]">Quantidade:</span>
                <span className="font-black text-amber-300 text-xs">
                  {qtd} un {selectedSize ? `(Aro ${selectedSize})` : ""}
                </span>
              </div>
            </div>

            <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-800/80 flex items-start gap-1.5">
              <Info size={13} className="text-amber-400 shrink-0 mt-0.5" />
              <span>
                A solicitação entrará na fila de injeção da fábrica. A equipe da área de produção decidirá na bancada se aproveita ceras disponíveis na gaveta ou injeta novas peças.
              </span>
            </div>
          </div>

          {/* BOTÕES DE AÇÃO */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || qtd <= 0}
              className="px-5 py-2.5 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white rounded-xl text-xs font-black shadow-md shadow-amber-600/30 transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <Layers size={14} className="text-amber-200" />
                  <span>Solicitar Produção ({qtd} un)</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
